import { AlertTriangle, ChevronRight, ExternalLink, Shield, ShieldAlert, ShieldCheck, Info } from "lucide-react";
import type { RiskLevel } from "@/lib/api";
import type { PriorityAction, BusinessCategory } from "@/lib/simplify-results";

interface PriorityActionsCardProps {
  actions: PriorityAction[];
  className?: string;
  /** Callback when user clicks on an action item to see details */
  onActionClick?: (action: PriorityAction) => void;
}

const SEVERITY_CONFIG: Record<RiskLevel, {
  icon: typeof AlertTriangle;
  color: string;
  bg: string;
  border: string;
  badge: string;
}> = {
  critical: {
    icon: ShieldAlert,
    color: "text-red-400",
    bg: "bg-red-500/10",
    border: "border-red-500/30",
    badge: "bg-red-500/20 text-red-400 border-red-500/40",
  },
  high: {
    icon: AlertTriangle,
    color: "text-orange-400",
    bg: "bg-orange-500/10",
    border: "border-orange-500/30",
    badge: "bg-orange-500/20 text-orange-400 border-orange-500/40",
  },
  medium: {
    icon: Shield,
    color: "text-amber-400",
    bg: "bg-amber-500/10",
    border: "border-amber-500/30",
    badge: "bg-amber-500/20 text-amber-400 border-amber-500/40",
  },
  low: {
    icon: ShieldCheck,
    color: "text-cyan-400",
    bg: "bg-cyan-500/10",
    border: "border-cyan-500/30",
    badge: "bg-cyan-500/20 text-cyan-400 border-cyan-500/40",
  },
  info: {
    icon: Info,
    color: "text-slate-400",
    bg: "bg-slate-500/10",
    border: "border-slate-500/30",
    badge: "bg-slate-500/20 text-slate-400 border-slate-500/40",
  },
};

const CATEGORY_LABELS: Record<BusinessCategory, string> = {
  email: "Email",
  website: "Website",
  domain: "Domain",
  exposure: "Exposure",
  brand: "Brand",
};

function ActionItem({
  action,
  index,
  onActionClick,
}: {
  action: PriorityAction;
  index: number;
  onActionClick?: (action: PriorityAction) => void;
}) {
  const config = SEVERITY_CONFIG[action.severity];
  const Icon = config.icon;

  return (
    <div
      onClick={() => onActionClick?.(action)}
      className={`
        group relative rounded-lg border ${config.border} ${config.bg}
        p-4 transition-all duration-200
        hover:border-opacity-60 hover:bg-opacity-20
        animate-fade-in-up
        ${onActionClick ? "cursor-pointer" : ""}
      `}
      style={{ animationDelay: `${index * 100}ms` }}
    >
      {/* Priority number badge */}
      <div
        className={`
          absolute -top-2 -left-2 w-6 h-6 rounded-full
          flex items-center justify-center
          text-xs font-bold font-mono
          border ${config.badge}
        `}
      >
        {action.priority}
      </div>

      <div className="flex items-start gap-3">
        {/* Severity icon */}
        <div className={`p-2 rounded-lg ${config.bg} border ${config.border} shrink-0`}>
          <Icon className={`h-4 w-4 ${config.color}`} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title */}
          <h4 className="font-medium text-foreground leading-tight mb-1">
            {action.title}
          </h4>

          {/* Description */}
          <p className="text-sm text-muted-foreground leading-relaxed">
            {action.description}
          </p>

          {/* Footer with category and learn more */}
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/50">
            <span
              className={`
                text-xs font-medium uppercase tracking-wider
                px-2 py-0.5 rounded-full
                bg-muted/50 text-muted-foreground
              `}
            >
              {CATEGORY_LABELS[action.category]}
            </span>

            {action.learnMoreUrl && (
              <a
                href={action.learnMoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={`
                  inline-flex items-center gap-1 text-xs font-medium
                  ${config.color} hover:underline
                  transition-colors
                `}
                onClick={(e) => e.stopPropagation()}
              >
                Learn how to fix
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        </div>

        {/* Chevron for visual affordance */}
        <ChevronRight
          className={`
            h-5 w-5 text-muted-foreground/50
            group-hover:text-muted-foreground
            group-hover:translate-x-0.5
            transition-all shrink-0
          `}
        />
      </div>
    </div>
  );
}

export function PriorityActionsCard({ actions, className = "", onActionClick }: PriorityActionsCardProps) {
  if (actions.length === 0) {
    return (
      <div className={`rounded-xl border border-border bg-card/50 p-6 ${className}`}>
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
            <ShieldCheck className="h-5 w-5 text-emerald-400" />
          </div>
          <div>
            <h3 className="font-semibold text-lg">No Priority Actions</h3>
            <p className="text-sm text-muted-foreground">
              Great news! No urgent security issues were found.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const urgentCount = actions.filter(
    (a) => a.severity === "critical" || a.severity === "high"
  ).length;

  return (
    <div className={`rounded-xl border border-border bg-card/50 overflow-hidden ${className}`}>
      {/* Header */}
      <div className="p-6 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className={`
                p-2 rounded-lg
                ${urgentCount > 0 ? "bg-red-500/10 border border-red-500/20" : "bg-amber-500/10 border border-amber-500/20"}
              `}
            >
              {urgentCount > 0 ? (
                <ShieldAlert className="h-5 w-5 text-red-400" />
              ) : (
                <Shield className="h-5 w-5 text-amber-400" />
              )}
            </div>
            <div>
              <h3 className="font-semibold text-lg">Priority Actions</h3>
              <p className="text-sm text-muted-foreground">
                {urgentCount > 0 ? (
                  <>
                    <span className="text-red-400 font-medium">{urgentCount} urgent</span>
                    {" "}
                    {urgentCount === 1 ? "issue needs" : "issues need"} immediate attention
                  </>
                ) : (
                  `${actions.length} ${actions.length === 1 ? "item" : "items"} to review`
                )}
              </p>
            </div>
          </div>

          {/* Count badge */}
          <div
            className={`
              px-3 py-1.5 rounded-full font-mono text-sm font-bold
              ${urgentCount > 0
                ? "bg-red-500/20 text-red-400 border border-red-500/30"
                : "bg-muted text-muted-foreground border border-border"
              }
            `}
          >
            {actions.length}
          </div>
        </div>
      </div>

      {/* Actions list */}
      <div className="px-6 pb-6 space-y-3">
        {actions.map((action, index) => (
          <ActionItem
            key={action.id}
            action={action}
            index={index}
            onActionClick={onActionClick}
          />
        ))}
      </div>
    </div>
  );
}

export default PriorityActionsCard;
